// Markdown parsing for agent replies (PHA-1155).
//
// Extracted from MessageBubble so it can be unit-tested, and extended beyond
// the original slim dialect. The agent now writes longer, report-style answers,
// and anything the parser did not know fell through as one run-on paragraph:
// numbered steps, code/SQL blocks and emphasis all collapsed together.
//
// Deliberately still hand-rolled rather than react-markdown: that package is
// ESM-only and does not load under CRA's Jest config, and the dialect here is
// small and fully covered by markdown.test.ts.
//
// Blocks:
//   # / ## / ###  heading (h1 renders as h2)
//   - item / • item / * item   bullet list
//   1. item / 1) item          numbered list
//   | a | b |  + separator     pipe table
//   ``` fenced code ```
//   > quote
//   --- / ***                  horizontal rule
//   {{table:tbl_N}}            table reference (live stream only; resolved
//                              server-side into a structured part at the end)
//   anything else              paragraph
//
// Inline tokens (see tokenizeInline): **bold**, *italic* / _italic_, `code`,
// [text](url), bare URLs, MRN ids, blood-pressure readings.

export type MdBlock =
  | { kind: 'heading'; level: 2 | 3; text: string }
  | { kind: 'bullet'; items: string[] }
  | { kind: 'ordered'; start: number; items: string[] }
  | { kind: 'table'; headers: string[]; rows: string[][] }
  | { kind: 'code'; lang: string; text: string }
  | { kind: 'quote'; text: string }
  | { kind: 'hr' }
  | { kind: 'tableRef'; id: string }
  | { kind: 'para'; text: string };

const HEADING_RE = /^(#{1,3})\s+(.*)$/;
const BULLET_RE = /^\s*[-•*]\s+/;
const ORDERED_RE = /^\s*(\d{1,3})[.)]\s+/;
const FENCE_RE = /^\s*```\s*([A-Za-z0-9_+-]*)\s*$/;
const QUOTE_RE = /^\s*>\s?/;
const HR_RE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;
const TABLE_REF_RE = /^\s*\{\{\s*table\s*:\s*(tbl_\d+)\s*\}\}\s*$/;
const TABLE_SEP_RE = /^\s*\|?\s*:?-{2,}/;

function isTableStart(lines: string[], i: number): boolean {
  return lines[i].includes('|') && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1]);
}

function startsBlock(lines: string[], i: number): boolean {
  const line = lines[i];
  return (
    HEADING_RE.test(line) ||
    // A "- " line after HR check: `---` alone is a rule, not a bullet.
    (BULLET_RE.test(line) && !HR_RE.test(line)) ||
    ORDERED_RE.test(line) ||
    FENCE_RE.test(line) ||
    QUOTE_RE.test(line) ||
    HR_RE.test(line) ||
    TABLE_REF_RE.test(line) ||
    isTableStart(lines, i)
  );
}

function parseRow(row: string): string[] {
  const trimmed = row.trim().replace(/^\|/, '').replace(/\|$/, '');
  // Split on pipes that are not escaped (build_table escapes "|" inside cells).
  return trimmed.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, '|'));
}

export function parseMarkdownBlocks(src: string): MdBlock[] {
  const lines = (src || '').replace(/\r\n/g, '\n').split('\n');
  const blocks: MdBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i++;
      continue;
    }

    const fence = line.match(FENCE_RE);
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !FENCE_RE.test(lines[i])) {
        body.push(lines[i]);
        i++;
      }
      i++; // closing fence (or EOF while still streaming)
      blocks.push({ kind: 'code', lang: fence[1] || '', text: body.join('\n') });
      continue;
    }

    const ref = line.match(TABLE_REF_RE);
    if (ref) {
      blocks.push({ kind: 'tableRef', id: ref[1] });
      i++;
      continue;
    }

    if (HR_RE.test(line)) {
      blocks.push({ kind: 'hr' });
      i++;
      continue;
    }

    const heading = line.match(HEADING_RE);
    if (heading) {
      blocks.push({
        kind: 'heading',
        level: heading[1].length === 3 ? 3 : 2,
        text: heading[2].trim(),
      });
      i++;
      continue;
    }

    if (BULLET_RE.test(line)) {
      const items: string[] = [];
      while (i < lines.length && BULLET_RE.test(lines[i]) && !HR_RE.test(lines[i])) {
        items.push(lines[i].replace(BULLET_RE, '').trim());
        i++;
      }
      blocks.push({ kind: 'bullet', items });
      continue;
    }

    const ordered = line.match(ORDERED_RE);
    if (ordered) {
      const start = Number(ordered[1]) || 1;
      const items: string[] = [];
      while (i < lines.length && ORDERED_RE.test(lines[i])) {
        items.push(lines[i].replace(ORDERED_RE, '').trim());
        i++;
      }
      blocks.push({ kind: 'ordered', start, items });
      continue;
    }

    if (QUOTE_RE.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && QUOTE_RE.test(lines[i])) {
        quoted.push(lines[i].replace(QUOTE_RE, ''));
        i++;
      }
      blocks.push({ kind: 'quote', text: quoted.join(' ').trim() });
      continue;
    }

    if (isTableStart(lines, i)) {
      const headers = parseRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(parseRow(lines[i]));
        i++;
      }
      blocks.push({ kind: 'table', headers, rows });
      continue;
    }

    const paraLines: string[] = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !startsBlock(lines, i)) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push({ kind: 'para', text: paraLines.join(' ').trim() });
  }

  return blocks;
}

export type InlineToken =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; text: string; href: string }
  | { kind: 'url'; href: string }
  | { kind: 'mrn'; text: string }
  | { kind: 'bp'; text: string };

// Order matters: code first (its contents are literal), then link before bare
// URL, bold before italic. The BP look-arounds keep "2025/12" and "1/2/3" out.
const INLINE_RE =
  /(`[^`\n]+`|\*\*[^*]+\*\*|\[[^\]]+\]\(https?:\/\/[^)\s]+\)|https?:\/\/[^\s)\]]+|(?<![\w*])\*[^*\s][^*\n]*?\*(?![\w*])|(?<![\w_])_[^_\s][^_\n]*?_(?![\w_])|MRN[A-Z0-9]{4,12}|(?<![\d/])\d{2,3}\s*\/\s*\d{2,3}(?![\d/]))/g;

export function tokenizeInline(text: string): InlineToken[] {
  const out: InlineToken[] = [];
  if (!text) return out;
  const re = new RegExp(INLINE_RE.source, 'g');
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ kind: 'text', text: text.slice(last, m.index) });
    const tok = m[0];
    if (tok.startsWith('`')) {
      out.push({ kind: 'code', text: tok.slice(1, -1) });
    } else if (tok.startsWith('**')) {
      out.push({ kind: 'bold', text: tok.slice(2, -2) });
    } else if (tok.startsWith('[')) {
      const lm = tok.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
      out.push(lm ? { kind: 'link', text: lm[1], href: lm[2] } : { kind: 'text', text: tok });
    } else if (/^https?:\/\//.test(tok)) {
      out.push({ kind: 'url', href: tok });
    } else if ((tok.startsWith('*') && tok.endsWith('*')) || (tok.startsWith('_') && tok.endsWith('_'))) {
      out.push({ kind: 'italic', text: tok.slice(1, -1) });
    } else if (tok.startsWith('MRN')) {
      out.push({ kind: 'mrn', text: tok });
    } else {
      out.push({ kind: 'bp', text: tok });
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
}
